import { ArrowRight, Check, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { usePricingCheckout } from "./usePricingCheckout";

export function PricingSection({ settings, user }) {
  const { dynamicPricing, startCheckout, checkoutLoading, checkoutError } = usePricingCheckout(settings, user);

  return (
    <section className="py-20 md:py-24 border-y border-navy-100 bg-navy-band" id="pricing">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="overline mb-3">Pricing</p>
          <h2 className="h-display text-3xl md:text-5xl text-navy-900" data-testid="pricing-headline">
            {settings.pricing_headline}
          </h2>
          <p className="mt-5 text-lg text-navy-600" data-testid="pricing-subheadline">
            {settings.pricing_subheadline}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 max-w-6xl mx-auto items-center">
          {dynamicPricing.map((p) => (
            <div
              key={p.id}
              className={`relative rounded-2xl p-7 flex flex-col bg-white border transition-all ${
                p.featured
                  ? "border-emerald-400 ring-4 ring-emerald-200/50 shadow-[0_0_40px_-8px_rgba(31,175,139,0.35)] lg:scale-[1.04] lg:py-9"
                  : "border-navy-100 shadow-card hover:shadow-card-hover"
              }`}
              data-testid={`home-pricing-${p.id}`}
            >
              {p.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] font-mono uppercase tracking-widest bg-emerald-500 text-white rounded-full shadow-[0_0_20px_rgba(31,175,139,0.6)]" data-testid={`home-pricing-badge-${p.id}`}>
                  {p.badge}
                </span>
              )}

              <div>
                <h3 className="h-display text-2xl text-navy-900">{p.name}</h3>
                <p className="mt-2 text-sm text-navy-500">{p.description}</p>
              </div>

              <div className="mt-6 pb-6 border-b border-navy-100">
                <div className="flex items-baseline gap-2">
                  <span className="h-display text-5xl text-navy-900">{p.price}</span>
                  <span className="text-sm text-navy-500 font-mono">/ {p.period}</span>
                </div>
              </div>

              <ul className="mt-6 space-y-3 flex-1">
                {p.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-[14px] text-navy-700">
                    <Check className={`w-4 h-4 shrink-0 mt-0.5 ${p.featured ? "text-emerald-500" : "text-emerald-600"}`} strokeWidth={2.5} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              {user?.subscription_tier === p.id ? (
                <button
                  disabled
                  data-testid={`home-pricing-cta-${p.id}`}
                  className="mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium rounded-lg border border-navy-200 bg-navy-50 text-navy-500 cursor-not-allowed"
                >
                  Current plan
                </button>
              ) : (
                <div className="mt-8 flex flex-col gap-2.5">
                  <Link
                    to="/demo"
                    data-testid={`home-pricing-cta-demo-${p.id}`}
                    className={`inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium rounded-lg transition-all ${
                      p.featured
                        ? "bg-emerald-500 hover:bg-emerald-600 text-white shadow-[0_4px_24px_rgba(31,175,139,0.45)] hover:shadow-[0_6px_30px_rgba(31,175,139,0.55)]"
                        : "bg-emerald-500 hover:bg-emerald-600 text-white shadow-[0_2px_14px_rgba(31,175,139,0.35)]"
                    }`}
                  >
                    Book a Demo <ArrowRight className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={() => startCheckout(p.id)}
                    disabled={checkoutLoading === p.id}
                    data-testid={`home-pricing-cta-${p.id}`}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg border border-navy-200 hover:border-navy-300 hover:bg-navy-50 text-navy-700 transition-all disabled:opacity-60"
                  >
                    {checkoutLoading === p.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>Start Free Trial</>
                    )}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {checkoutError && (
          <p className="mt-6 text-center text-sm text-rose-600" data-testid="home-pricing-error">{checkoutError}</p>
        )}

        <p className="mt-10 text-center text-sm text-navy-500">
          All plans start with your first month free. Cancel anytime before day 30 and you won't be charged.
        </p>

        <div className="mt-4 text-center">
          <Link to="/pricing" data-testid="home-pricing-compare-link" className="inline-flex items-center gap-1.5 text-sm text-navy-600 hover:text-navy-900">
            Compare full feature breakdown <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
